// src/components/instructor/OrgAccessSettings.js
//
// Pass 58C — Organisation → Settings → Access & Invitations. This is the CANONICAL, single place to
// administer account access: add an approved login email, choose the permission role, prepare/send/resend
// an invitation, see pending/active/disabled state, and disable/restore/revoke access. Owner/admin ONLY
// (ops cannot administer access). It reuses the SAME canonical invite model (organizationInvites +
// TeamAccessControls) — Team no longer carries a competing invite/role/revoke surface; it links here.

import React from 'react';
import { useAuth } from '../../AuthContext';
import { canManageOrganization } from '../../utils/platformAccess';
import { teamAccessUiState, TEAM_UI_STATE, TEAM_UI_STATE_LABEL, permissionRoleOf, PERMISSION_LABEL } from '../../utils/teamAccess';
import TeamAccessControls from './ops/TeamAccessControls';

const V = { t1: '#1A1917', t2: '#6B6860', t3: '#9E9B95', border: '#E2E0DB', card: '#fff' };

export default function OrgAccessSettings({ members = [], organizationId = '' }) {
  const { userData } = useAuth() || {};
  const canManage = canManageOrganization(userData, organizationId);

  if (!canManage) {
    return (
      <div data-testid="access-settings-denied" style={{ padding: 20, color: V.t2, fontSize: 14 }}>
        Access &amp; invitations can only be administered by an organisation owner or admin.
      </div>
    );
  }

  // Real people first (skip obvious test/placeholder rows if flagged), sorted by name.
  const rows = [...members]
    .filter((m) => m && (m.userId || m.id))
    .sort((a, b) => String(a.name || a.displayName || '').localeCompare(String(b.name || b.displayName || '')));

  // Pass 83B §33/§68 — an at-a-glance activation-readiness summary so the operator can see how many people
  // are already active, ready to invite, waiting on acceptance, or still need an email — WITHOUT initiating
  // any bulk outreach (invitations are prepared one-by-one below).
  const tally = rows.reduce((acc, m) => { const s = teamAccessUiState(m); acc[s] = (acc[s] || 0) + 1; return acc; }, {});
  const summary = [
    { key: TEAM_UI_STATE.ACTIVE, label: 'Active login', color: '#16A34A' },
    { key: TEAM_UI_STATE.READY_TO_INVITE, label: 'Ready to invite', color: '#2563EB' },
    { key: TEAM_UI_STATE.INVITE_PENDING, label: 'Invitation pending', color: '#D97706' },
    { key: TEAM_UI_STATE.EMAIL_REQUIRED, label: 'Email required', color: V.t2 },
    { key: TEAM_UI_STATE.DISABLED, label: 'Disabled', color: V.t3 },
  ].filter((s) => tally[s.key]);

  return (
    <div data-testid="org-access-settings" style={{ fontFamily: "'DM Sans',sans-serif", color: V.t1 }}>
      <h3 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 800 }}>Access &amp; Invitations</h3>
      <p style={{ margin: '0 0 16px', fontSize: 13, color: V.t2, maxWidth: 640 }}>
        Add an approved login email and choose a permission role. Invite links are available now — prepare an
        invitation and copy its secure <code>/invite/&lt;code&gt;</code> link to share (each person sets their own
        password; no temporary password is ever issued). Email delivery can be enabled later. Owner/admin only.
      </p>

      {summary.length > 0 && (
        <div data-testid="access-readiness-summary" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
          {summary.map((s) => (
            <span key={s.key} style={{ fontSize: 12, fontWeight: 700, color: s.color, background: '#F5F4F0', border: `1px solid ${V.border}`, borderRadius: 20, padding: '4px 11px' }}>
              {tally[s.key]} {s.label}
            </span>
          ))}
        </div>
      )}

      <div style={{ border: `1px solid ${V.border}`, borderRadius: 12, overflow: 'hidden' }}>
        {rows.length === 0 ? (
          <div style={{ padding: 20, color: V.t3, fontSize: 13 }}>No members yet.</div>
        ) : rows.map((m) => {
          const state = teamAccessUiState(m);
          const role = permissionRoleOf(m);
          return (
            <div key={m.id || m.userId} data-testid={`access-row-${m.userId || m.id}`}
              style={{ padding: '12px 16px', borderBottom: `1px solid ${V.border}`, background: V.card }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ minWidth: 200 }}>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>{m.name || m.displayName || m.userId}</div>
                  <div style={{ fontSize: 11.5, color: V.t3 }}>
                    {TEAM_UI_STATE_LABEL[state]} · {PERMISSION_LABEL[role] || role}{m.email ? ` · ${m.email}` : ''}
                  </div>
                </div>
                {/* The canonical mutation surface (invite/resend/role/revoke) — owner/admin-gated inside. */}
                <TeamAccessControls member={m} organizationId={organizationId} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
